import React, { useEffect, useRef, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useDrag } from '@use-gesture/react';
import { useBreakpoint } from '@/hooks/useBreakpoint';
import { useTheme } from '@/contexts/ThemeContext';
import SoundUtils from '@/utils/soundUtils';
import { bookAnalytics } from '@/services/bookAnalytics';
import {
  inputMethodFromEvent,
  type AnalyticsInputMethod,
  type AnalyticsTrigger,
  type InteractionContext,
  type VoiceEndContext,
  type VoiceEntrySurface,
  type VoiceStartContext,
} from '@/services/analyticsContext';
import type { Book } from '@/utils/bookRouting';
import KeyboardIcon from './icons/KeyboardIcon';
import VoiceConversationIcon from './icons/VoiceConversationIcon';

type ButtonSize = 'base' | 'sm' | 'md' | 'lg' | 'xl';
export type ConversationMode = 'voice' | 'text';
type AlephEntryVariant = 'default' | 'expanded';

/** Horizontal drag distance (px) past which the mobile button switches mode. */
const SWITCH_THRESHOLD = 24;

interface AlephEntryProps {
  book: Book;
  /**
   * Read at event time, not at render: the players memoise around currentTime,
   * so a number passed as a prop would be frozen at the last re-render.
   */
  getProgressSeconds: () => number;
  analyticsSurface: VoiceEntrySurface;
  /** Voice: start the realtime connection. */
  onConnect: (context: VoiceStartContext) => Promise<boolean>;
  /** Voice: tear the connection down. */
  onDisconnect: (context: VoiceEndContext) => Promise<void>;
  isConnected: boolean;
  connectionStatus: string;
  isBuffering: boolean;
  /** Text: open the chat sheet. Omitted → voice only. */
  onType?: (context: InteractionContext) => void;
  /** Hide the mobile text composer while the full-screen text chat owns it. */
  hideMobileTextComposer?: boolean;
  size?: ButtonSize;
  /** Drop the Speak/Type text labels (for tight rows like the player controls). */
  compact?: boolean;
  /** Full-width treatment: the reader's bottom input, and the docked player on desktop. */
  variant?: AlephEntryVariant;
  /** Unsent draft text to preview in the text-mode button when the chat sheet is closed. */
  draftText?: string;
  /** Match compact controls to the neutral conversation surface. */
  matchConversationSurface?: boolean;
  /** Optional shared state used when player surfaces replace one another. */
  mode?: ConversationMode;
  onModeChange?: (mode: ConversationMode) => void;
}

const Spinner: React.FC<{ className?: string }> = ({ className }) => (
  <svg className={`animate-spin ${className || 'h-5 w-5'}`} xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
    <path
      className="opacity-75"
      fill="currentColor"
      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
    />
  </svg>
);

const AI_BORDER_SEGMENTS = [
  'base',
  'fade-start-1',
  'fade-start-2',
  'fade-start-3',
  'fade-start-4',
  'core-start',
  'core-end',
  'fade-end-4',
  'fade-end-3',
  'fade-end-2',
  'fade-end-1',
] as const;

/** Identical SVG paths keep the moving accent and its base rasterized alike. */
const AnimatedAiBorder: React.FC<{ radius: number; strokeWidth: number; baseColor: string; accentColor: string }> = ({
  radius,
  strokeWidth,
  baseColor,
  accentColor,
}) => (
  <svg
    aria-hidden="true"
    focusable="false"
    className="aleph-ai-border pointer-events-none absolute"
    style={{
      '--aleph-border-inset': `-${strokeWidth}px`,
      '--aleph-border-expansion': `${strokeWidth * 2}px`,
      '--aleph-border-stroke-width': `${strokeWidth}px`,
      '--aleph-border-base': baseColor,
      '--aleph-border-accent': accentColor,
    } as React.CSSProperties}
  >
    {AI_BORDER_SEGMENTS.map((segment) => (
      <rect
        key={segment}
        className={`aleph-ai-border__segment aleph-ai-border__segment--${segment}`}
        pathLength="100"
        x={strokeWidth / 2}
        y={strokeWidth / 2}
        style={{
          width: `calc(100% - ${strokeWidth}px)`,
          height: `calc(100% - ${strokeWidth}px)`,
        }}
        rx={radius}
        ry={radius}
      />
    ))}
  </svg>
);

/**
 * A single "Aleph" affordance that replaces the separate voice and text
 * buttons, with voice as the default.
 *
 * - Mobile: one button; swipe left/right toggles audio ⇄ text (the icon shows
 *   the active mode), tap performs it.
 * - Desktop: a fat pill split in two — Speak | Type — sharing a divider.
 *
 * A live voice session collapses both layouts to a single End control,
 * preserving the connect / connecting / disconnect states.
 */
const AlephEntry: React.FC<AlephEntryProps> = ({
  book,
  getProgressSeconds,
  analyticsSurface,
  onConnect,
  onDisconnect,
  isConnected,
  connectionStatus,
  isBuffering,
  onType,
  hideMobileTextComposer = false,
  size = 'lg',
  compact = false,
  variant = 'default',
  draftText,
  matchConversationSurface = false,
  mode: controlledMode,
  onModeChange,
}) => {
  const bp = useBreakpoint();
  const { theme } = useTheme();
  const isDark = theme === 'dark';
  // Keep the desktop split Speak | Type pill aligned with the 1024px `lg`
  // breakpoint; smaller viewports use the circular swipeable action.
  const isDesktop = bp === 'lg' || bp === 'xl' || bp === '2xl';

  const [isConnecting, setIsConnecting] = useState(false);
  const [isDisconnecting, setIsDisconnecting] = useState(false);
  const [localMode, setLocalMode] = useState<ConversationMode>('voice');
  const mode = controlledMode ?? localMode;
  const suppressModeActionUntilRef = useRef(0);

  useEffect(() => {
    if (isConnected || isBuffering) setIsConnecting(false);
  }, [isConnected, isBuffering]);

  const isActive = isConnected || isBuffering;
  const showSpinner = isConnecting || isDisconnecting;
  const canSwitch = !!onType && !isActive && !showSpinner;

  const interactionContext = (
    trigger: AnalyticsTrigger,
    inputMethod: AnalyticsInputMethod,
  ): InteractionContext => ({
    surface: analyticsSurface,
    trigger,
    input_method: inputMethod,
  });

  const startVoice = async (
    inputMethod: AnalyticsInputMethod = 'unknown',
    trigger: AnalyticsTrigger = 'button',
  ) => {
    SoundUtils.unlock(); // unlock audio on the user gesture so the beep can play
    setIsConnecting(true);
    try {
      const ok = await onConnect(interactionContext(trigger, inputMethod) as VoiceStartContext);
      if (!ok) setIsConnecting(false);
    } catch {
      setIsConnecting(false);
    }
  };

  const endVoice = async (
    inputMethod: AnalyticsInputMethod = 'unknown',
    trigger: AnalyticsTrigger = 'button',
  ) => {
    setIsDisconnecting(true);
    try {
      await onDisconnect({
        ...interactionContext(trigger, inputMethod),
        end_reason: 'user_stop',
      } as VoiceEndContext);
    } finally {
      setIsDisconnecting(false);
    }
  };

  const doVoice = (
    inputMethod: AnalyticsInputMethod = 'unknown',
    trigger: AnalyticsTrigger = 'button',
  ) => {
    if (showSpinner) return;
    isActive ? endVoice(inputMethod, trigger) : startVoice(inputMethod, trigger);
  };

  // Mobile interaction handlers. These — and especially the useDrag hook —
  // MUST run on every render, so they live above the `if (isWide)` early return
  // below. Previously useDrag sat after that return; rotating a phone past the
  // `md` (768px) breakpoint flipped `isWide`, changing the hook count between
  // renders and crashing with React #300 ("rendered fewer hooks than expected").
  // bind/switchTo/handleTap are only *used* in the mobile branch.
  const switchTo = (
    next: ConversationMode,
    trigger: AnalyticsTrigger = 'button',
    inputMethod: AnalyticsInputMethod = 'unknown',
  ) => {
    const now = Date.now();
    if (now < suppressModeActionUntilRef.current) return;
    if (!canSwitch || next === mode) return;
    bookAnalytics.track(
      'Voice/Text Mode Switched',
      book,
      {
        from: mode,
        to: next,
        is_desktop: isDesktop,
        ...interactionContext(trigger, inputMethod),
      },
      getProgressSeconds(),
    );
    suppressModeActionUntilRef.current = now + 350;
    if (controlledMode === undefined) setLocalMode(next);
    onModeChange?.(next);
  };

  const handleTap = (inputMethod: AnalyticsInputMethod = 'touch') => {
    if (Date.now() < suppressModeActionUntilRef.current) return;
    if (isActive || showSpinner) {
      doVoice(inputMethod);
      return;
    }
    // Consumers such as the docked mobile player omit `onType` to make this
    // control voice-only. The local mode may still be `text` after resizing
    // from a desktop layout, so do not let that stale state disable voice.
    mode === 'voice' || !onType
      ? startVoice(inputMethod)
      : onType(interactionContext('button', inputMethod));
  };

  // Distance-based (not velocity): a slow drag past the threshold switches mode,
  // so it works with a deliberate drag and devtools mouse emulation, not just a
  // fast flick. axis:'x' keeps it from fighting the player's vertical swipe.
  const bind = useDrag(
    ({ tap, last, movement: [mx], event }) => {
      const inputMethod = inputMethodFromEvent({ nativeEvent: event });
      if (tap) {
        handleTap(inputMethod);
        return;
      }
      if (last && canSwitch) {
        // With only two modes, either horizontal direction switches to the
        // other one. This also lets a user repeat the same swipe to move back.
        if (Math.abs(mx) >= SWITCH_THRESHOLD) {
          switchTo(mode === 'voice' ? 'text' : 'voice', 'swipe', inputMethod);
        }
      }
    },
    { filterTaps: true, axis: 'x' }
  );

  // --- sizing -------------------------------------------------------------
  const heightMap: Record<ButtonSize, string> = {
    base: 'h-10 text-sm',
    sm: 'h-12 text-sm',
    md: 'h-14 text-base',
    lg: 'h-16 text-base',
    xl: 'h-18 text-lg',
  };
  const circleMap: Record<ButtonSize, string> = {
    base: 'w-10 h-10',
    sm: 'w-12 h-12',
    md: 'w-14 h-14',
    lg: 'w-16 h-16',
    xl: 'w-18 h-18',
  };
  const iconSize: 'sm' | 'md' = size === 'base' || size === 'sm' ? 'sm' : 'md';

  // Idle is blue-on-white in both themes (the voice button's surface is always
  // the light-mode colour); active fills blue with white content.
  const idleColor = 'bg-white border-blue-700 text-blue-700';
  const activeColor = 'bg-blue-700 border-blue-700 text-white';
  const pillColor = isActive ? activeColor : idleColor;
  const dividerColor = 'bg-blue-700/25';

  const VoiceIcon = <VoiceConversationIcon size={iconSize} />;
  const TextIcon = <KeyboardIcon size={iconSize === 'sm' ? 'md' : 'lg'} />;
  const renderModeActionButton = (isVoiceMode: boolean, buttonClassName = 'flex w-16 shrink-0 cursor-pointer items-center justify-center transition-colors') => {
    const toggleIcon = isVoiceMode
      ? <KeyboardIcon className="" size="lg" />
      : <VoiceConversationIcon className="" size={iconSize} />;
    const toggleMode: ConversationMode = isVoiceMode ? 'text' : 'voice';

    return (
      <button
        type="button"
        data-tutorial-id="player-text-toggle"
        onClick={(event) => {
          event.stopPropagation();
          switchTo(toggleMode, 'button', inputMethodFromEvent(event));
        }}
        aria-label={isVoiceMode ? 'Switch to text input' : 'Switch to voice input'}
        className={buttonClassName}
      >
        <span className={`flex h-10 w-10 items-center justify-center transition-colors ${
          isDark && matchConversationSurface ? 'text-gray-200' : 'text-blue-600'
        }`}>
          {toggleIcon}
        </span>
      </button>
    );
  };

  // ----------------------------------------------------------------------
  // Expanded: large action area + smaller mode toggle.
  // ----------------------------------------------------------------------
  if (variant === 'expanded') {
    const expandedBorder = 'border-[2px] border-blue-600';
    const conversationSurface = isDark
      ? 'border border-black-700 bg-black-900'
      : 'border-2 border-blue-600 bg-white';
    const conversationBorderWidth = isDark ? 1 : 2;
    const conversationBorderColor = isDark ? 'var(--color-black-700)' : 'var(--color-blue-600)';
    const conversationBorderAccentColor = isDark ? 'var(--color-black-500)' : '#8b22ff';
    const expandedHeightMap: Record<ButtonSize, string> = {
      base: 'h-12 text-base',
      sm: 'h-12 text-base',
      md: 'h-14 text-base',
      lg: 'h-14 text-base',
      xl: 'h-14 text-base',
    };
    const expandedComposerHeightMap: Record<ButtonSize, string> = {
      base: 'min-h-12 text-base',
      sm: 'min-h-12 text-base',
      md: 'min-h-14 text-base',
      lg: 'min-h-14 text-base',
      xl: 'min-h-14 text-base',
    };
    // Keep the path centreline half a stroke inside the pill's outer radius.
    // Explicit radii avoid SVG's automatic rx cap bowing into wide pills.
    const expandedOuterRadiusMap: Record<ButtonSize, number> = {
      base: 24,
      sm: 24,
      md: 28,
      lg: 28,
      xl: 28,
    };
    const conversationBorderRadius = expandedOuterRadiusMap[size] - conversationBorderWidth / 2;
    const expandedModeButtonClass = `flex shrink-0 self-center cursor-pointer items-center justify-center rounded-full transition-colors ${
      isDark && matchConversationSurface
        ? 'border border-black-700 bg-black-900 text-gray-200'
        : `bg-white text-blue-600 ${expandedBorder}`
    } ${expandedHeightMap[size]} aspect-square`;

    if (isActive || showSpinner || !onType) {
      return (
        <button
          type="button"
          data-tutorial-id="player-speak"
          onClick={(event) => doVoice(inputMethodFromEvent(event))}
          title={connectionStatus}
          aria-label={isActive ? 'End voice conversation' : 'Start voice conversation'}
          className={`aleph-entry relative flex w-full min-w-0 cursor-pointer items-center justify-center gap-2 rounded-full px-3 font-medium transition-colors sm:px-7 ${
            isDark ? 'text-white-custom' : 'text-blue-600'
          } ${conversationSurface} ${expandedHeightMap[size]} ${!isActive && !showSpinner ? 'aleph-ai-border-host' : ''}`}
        >
          {!isActive && !showSpinner && (
            <AnimatedAiBorder
              radius={conversationBorderRadius}
              strokeWidth={conversationBorderWidth}
              baseColor={conversationBorderColor}
              accentColor={conversationBorderAccentColor}
            />
          )}
          {showSpinner ? <Spinner /> : isActive ? null : VoiceIcon}
          <span className="truncate">{showSpinner ? (isConnecting ? 'Connecting…' : 'Ending…') : isActive ? 'End conversation' : 'Tap to speak with Aleph'}</span>
        </button>
      );
    }

    const isVoiceMode = mode === 'voice';
    const mainLabel = isVoiceMode ? 'Tap to speak with Aleph' : 'Tap to message Aleph...';
    const mainAriaLabel = isVoiceMode ? 'Start voice conversation' : 'Open text chat';

    if (!isDesktop && hideMobileTextComposer && !isVoiceMode) {
      // The chat sheet renders its own viewport-anchored composer. Keep this
      // row's height in the player layout so the controls above it do not drop
      // when the inline composer disappears during the sheet transition.
      return (
        <div
          aria-hidden="true"
          className={`aleph-entry w-full invisible pointer-events-none ${expandedHeightMap[size]}`}
        />
      );
    }

    if (!isVoiceMode) {
      const hasDraft = !!draftText?.trim();
      return (
        <div className="aleph-entry flex w-full items-stretch gap-2">
          <button
            type="button"
            onClick={(event) => onType(interactionContext('button', inputMethodFromEvent(event)))}
            aria-label={mainAriaLabel}
            className={`aleph-ai-border-host relative flex min-w-0 flex-1 cursor-text items-center rounded-[28px] px-5 text-left transition-colors ${conversationSurface} ${expandedComposerHeightMap[size]}`}
          >
            <AnimatedAiBorder
              radius={conversationBorderRadius}
              strokeWidth={conversationBorderWidth}
              baseColor={conversationBorderColor}
              accentColor={conversationBorderAccentColor}
            />
            {hasDraft ? (
              <span className={`block w-full truncate leading-5 ${isDark ? 'text-white-custom' : 'text-gray-800'}`}>{draftText}</span>
            ) : (
              <span className={`block w-full truncate leading-5 ${isDark ? 'text-black-200' : 'text-gray-500'}`}>{mainLabel}</span>
            )}
          </button>
          {renderModeActionButton(isVoiceMode, expandedModeButtonClass)}
        </div>
      );
    }

    return (
      <div className="aleph-entry flex w-full items-stretch gap-2">
        <div data-tutorial-id="player-speak" className={`aleph-ai-border-host relative flex min-w-0 flex-1 items-stretch rounded-[28px] ${conversationSurface} ${expandedHeightMap[size]}`}>
          <AnimatedAiBorder
            radius={conversationBorderRadius}
            strokeWidth={conversationBorderWidth}
            baseColor={conversationBorderColor}
            accentColor={conversationBorderAccentColor}
          />
          <button
            type="button"
            onClick={(event) => startVoice(inputMethodFromEvent(event))}
            aria-label={mainAriaLabel}
            className={`flex min-w-0 flex-1 cursor-pointer items-center justify-center px-4 text-center font-normal transition-colors ${
              isDark ? 'text-white-custom' : 'text-gray-800'
            }`}
          >
            <span className="block truncate leading-5">{mainLabel}</span>
          </button>
        </div>
        {renderModeActionButton(isVoiceMode, expandedModeButtonClass)}
      </div>
    );
  }

  // ----------------------------------------------------------------------
  // Wide (tablet+): split pill (idle) / single End-or-Connecting control (busy)
  // ----------------------------------------------------------------------
  if (isDesktop) {
    if (isActive || showSpinner || !onType) {
      return (
        <button
          type="button"
          onClick={(event) => doVoice(inputMethodFromEvent(event))}
          title={connectionStatus}
          aria-label={isActive ? 'End voice conversation' : 'Speak with Aleph'}
          className={`aleph-entry flex cursor-pointer items-center justify-center gap-2 rounded-full border-2 font-medium px-7 ${heightMap[size]} ${pillColor}`}
        >
          {showSpinner ? <Spinner /> : isActive ? null : VoiceIcon}
          <span>{showSpinner ? (isConnecting ? 'Connecting…' : 'Ending…') : isActive ? 'End conversation' : 'Speak'}</span>
        </button>
      );
    }

    return (
      <div className={`aleph-entry flex items-stretch rounded-full border-2 overflow-hidden font-medium ${heightMap[size]} ${pillColor}`}>
        <button
          type="button"
          onClick={(event) => doVoice(inputMethodFromEvent(event))}
          title="Speak with Aleph"
          aria-label="Speak with Aleph"
          className={`flex items-center justify-center cursor-pointer hover:bg-blue-700/5 ${compact ? 'px-5' : 'gap-2 pl-6 pr-5'}`}
        >
          {VoiceIcon}
          {!compact && <span>Speak</span>}
        </button>
        <span aria-hidden="true" className={`w-px my-2.5 ${dividerColor}`} />
        <button
          type="button"
          onClick={(event) => onType(interactionContext('button', inputMethodFromEvent(event)))}
          title="Text with Aleph"
          aria-label="Text with Aleph"
          className={`flex items-center justify-center cursor-pointer hover:bg-blue-700/5 ${compact ? 'px-5' : 'gap-2 pr-6 pl-5'}`}
        >
          {TextIcon}
          {!compact && <span>Type</span>}
        </button>
      </div>
    );
  }

  // ----------------------------------------------------------------------
  // Mobile: one button; swipe to switch mode, tap to act. (The switchTo/
  // handleTap handlers and the useDrag hook are defined above, before the
  // isWide early return, so the hook order stays stable across breakpoints.)
  // ----------------------------------------------------------------------
  const isVoiceOnly = !onType;
  const activeIcon = isActive || isVoiceOnly || mode === 'voice' ? VoiceIcon : TextIcon;
  const mobileColor = matchConversationSurface
    ? isDark
      ? 'border-black-700 bg-black-900 text-gray-200'
      : `border-blue-600 bg-white ${isVoiceOnly ? 'text-blue-600' : 'text-gray-800'}`
    : pillColor;
  const mobileBorderWidth = matchConversationSurface && isDark ? 'border' : 'border-2';

  return (
    <div className="aleph-entry relative flex flex-col items-center">
      <button
        type="button"
        data-tutorial-id="player-speak"
        {...bind()}
        title={connectionStatus}
        aria-label={
          isActive
            ? 'End voice conversation'
            : isVoiceOnly
              ? 'Speak to Aleph'
              : mode === 'voice'
              ? 'Speak to Aleph — swipe to switch mode'
              : 'Open text chat — swipe to switch mode'
        }
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            handleTap('keyboard');
          } else if (e.key === 'ArrowLeft') {
            switchTo('voice', 'keyboard', 'keyboard');
          } else if (e.key === 'ArrowRight') {
            switchTo('text', 'keyboard', 'keyboard');
          }
        }}
        className={`touch-none select-none rounded-full flex items-center justify-center cursor-pointer ${circleMap[size]} ${mobileBorderWidth} ${mobileColor}`}
      >
        {showSpinner ? (
          <Spinner />
        ) : (
          <span className="relative flex h-8 w-8 items-center justify-center">
            <AnimatePresence initial={false}>
              <motion.span
                key={isActive ? 'active' : mode}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.15 }}
                className="absolute inset-0 flex items-center justify-center"
              >
                {activeIcon}
              </motion.span>
            </AnimatePresence>
          </span>
        )}
      </button>

      {/* Mode indicator + tap fallback: swipe to switch, or tap the dots. */}
      {canSwitch && (
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            switchTo(
              mode === 'voice' ? 'text' : 'voice',
              'button',
              inputMethodFromEvent(e),
            );
          }}
          aria-label={`Switch to ${mode === 'voice' ? 'text' : 'voice'} mode`}
          className="absolute -bottom-4 flex gap-1.5 p-1.5 cursor-pointer"
        >
          <span className={`w-1.5 h-1.5 rounded-full transition-colors ${mode === 'voice' ? 'bg-blue-700' : 'bg-blue-700/30'}`} />
          <span className={`w-1.5 h-1.5 rounded-full transition-colors ${mode === 'text' ? 'bg-blue-700' : 'bg-blue-700/30'}`} />
        </button>
      )}
    </div>
  );
};

export default AlephEntry;
